/**
 * First run (no fan yet, director available): basketball before any form. A featured story with
 * its real players, the real games around it, and one action — "Catch me up · 60s" — that lands
 * a fresh fan in a personal catch-up within seconds. The finish stays unspoiled so the fan makes
 * their own call. The longer team setup remains one tap away.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { directorTeamName, type DirectorLanguage, type Fan, type GameMeta, type LiveGame } from '@clutch/core';
import { useClient } from '../client';
import { navigate } from '../router';
import { teamAccent } from '../ui/color';
import { Headshot, TeamLogo } from '../ui/Media';
import { buildFeed, type FeedCard } from '../home/feed';
import { htmlLang, languageLabel, pick, type UiLanguage } from '../director/i18n';
import { browserLanguage, cardDate, cardStatus, weekCopy } from '../home/language';
import { currentWeek } from '../home/personal';
import { discoveryRows, featuredGame } from '../home/featured';
import { DiscoveryRows } from '../home/DiscoveryRows';
import { useLanguages } from '../director/LangToggle';

const copy = (l: string) => ({
  kicker: pick({ en: 'Every NBA game, your story.', hi: 'हर NBA मैच, आपकी कहानी।', zh: '每一场NBA，都是你的故事。' }, l),
  thesis: pick({ en: 'The NBA broadcasts the game. Clutch directs your experience.', hi: 'NBA मैच दिखाता है। Clutch उसे आपकी कहानी बनाता है।', zh: 'NBA转播比赛，Clutch为你导演观赛体验。' }, l),
  cta: pick({ en: 'Catch me up', hi: 'मुझे अपडेट करें', zh: '帮我补课' }, l),
  sixty: pick({ en: '60 sec', hi: '60 सेकंड', zh: '60 秒' }, l),
  name: pick({ en: 'Your first name (optional)', hi: 'आपका नाम (वैकल्पिक)', zh: '你的名字（选填）' }, l),
  setup: pick({ en: 'Pick your team first', hi: 'पहले अपनी टीम चुनें', zh: '先选择你的球队' }, l),
  opening: pick({ en: 'Opening…', hi: 'खुल रहा है…', zh: '正在打开…' }, l),
  failed: pick({ en: 'Couldn’t open that game. Try again.', hi: 'मैच नहीं खुल सका। फिर कोशिश करें।', zh: '无法打开比赛，请重试。' }, l),
  retry: pick({ en: 'Try again', hi: 'फिर कोशिश करें', zh: '重试' }, l),
  live: pick({ en: 'LIVE', hi: 'लाइव', zh: '直播' }, l),
  guest: pick({ en: 'Guest', hi: 'मेहमान', zh: '访客' }, l),
});

export function FirstRun({ onStart, onSetup }: { onStart: (f: Fan) => void; onSetup: (language: DirectorLanguage) => void }) {
  const client = useClient();
  const langs = useLanguages();
  const [lang, setLang] = useState<UiLanguage>(() => {
    const n = (typeof navigator !== 'undefined' ? navigator.language : 'en').toLowerCase();
    return browserLanguage(n);
  });
  const l = langs.includes(lang) ? lang : 'en';
  const c = copy(l);
  const week = weekCopy(l);
  const [name, setName] = useState('');
  const [live, setLive] = useState<LiveGame[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [scheduleError, setScheduleError] = useState(false);
  const [partial, setPartial] = useState(false);
  const [replays, setReplays] = useState<GameMeta[]>();
  const [opening, setOpening] = useState<string>();
  const [err, setErr] = useState<string>();
  const [reload, setReload] = useState(0);
  const createdFan = useRef<Fan>();
  const starting = useRef(false);

  useEffect(() => {
    let alive = true;
    let loading = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setReplays(undefined);
    client.listGames().then((g) => alive && setReplays(g)).catch(() => alive && setReplays([]));
    setLoaded(false); setScheduleError(false);
    const refresh = () => {
      if (!alive || document.hidden || loading) return;
      loading = true;
      void client.liveGames().then((g) => {
        if (alive) { setLive(g); setScheduleError(false); setPartial(!!client.scheduleCoverage?.().missingDates.length); }
      }).catch(() => { if (alive) { setLive([]); setScheduleError(true); } }).finally(() => {
        loading = false;
        if (alive) { setLoaded(true); if (!document.hidden) timer = setTimeout(refresh, 10_000); }
      });
    };
    const visible = () => { clearTimeout(timer); refresh(); };
    document.addEventListener('visibilitychange', visible);
    refresh();
    return () => {
      alive = false;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [client, reload]);

  const feed = useMemo(() => buildFeed(live, replays ?? []), [live, replays]);
  // The featured slot comes from data (most dramatic recent final, else live) — never a fixed hero.
  const featured = featuredGame(feed.live);
  const rows = discoveryRows({ live: currentWeek(feed.live), classics: feed.classics, ...(featured ? { featuredId: featured.gameId } : {}) });

  const start = async (card: FeedCard, catchUp: boolean, direct = false) => {
    if (starting.current) return;
    starting.current = true;
    setOpening(card.key);
    setErr(undefined);
    try {
      const displayName = (name.trim() || c.guest).slice(0, 24);
      createdFan.current ??= await client.createFan({ displayName, budgetPerWeek: 3 });
      if (createdFan.current.displayName !== displayName) createdFan.current = await client.updateFan(createdFan.current.fanId, { displayName, budgetPerWeek: 3 });
      if (client.director) {
        const profile = await client.director.saveProfile(createdFan.current.fanId, { language: l });
        createdFan.current = { ...createdFan.current, directorProfile: profile, team: profile.followedTeams[0], player: profile.followedPlayers[0] };
      }
      const { roomId } = card.kind === 'classic'
        ? await client.createRoom({ gameId: card.gameId, startAtSeq: card.startAtSeq ?? 0, speed: 1 })
        : await client.createRoom({ gameId: card.gameId, ...(card.kind === 'final' && catchUp ? { mode: 'recap' as const } : { community: card.kind === 'live' || card.kind === 'tonight' }) });
      onStart(createdFan.current);
      navigate(catchUp ? `/room/${encodeURIComponent(roomId)}/catchup?lang=${l}${direct ? '&direct=1' : ''}` : `/room/${encodeURIComponent(roomId)}`);
    } catch {
      setErr(c.failed);
      setOpening(undefined);
    } finally {
      starting.current = false;
    }
  };

  return (
    <main className="first" data-testid="first-run" lang={htmlLang(l)}>
      <header className="first__top">
        <span className="wordmark">CLUTCH</span>
        {langs.length > 1 && (
          <div className="lang lang--sm" role="radiogroup" aria-label={pick({ en: 'Language', hi: 'भाषा', zh: '语言' }, l)}>
            {langs.map((x) => (
              <button key={x} data-testid={`first-lang-${x}`} role="radio" aria-checked={x === l} className={x === l ? 'is-on' : ''} onClick={() => setLang(x)} {...(x !== 'en' ? { lang: htmlLang(x) } : {})}>{languageLabel(x)}</button>
            ))}
          </div>
        )}
      </header>
      <h1 className="first__kicker">{c.kicker}</h1>
      <p className="first__thesis">{c.thesis}</p>

      {featured ? (
        <FeaturedStory card={featured} c={c} lang={l} busy={opening === featured.key} name={name} onName={setName} onGo={() => void start(featured, featured.kind !== 'tonight')} onDirect={featured.kind !== 'tonight' ? () => void start(featured, true, true) : undefined} />
      ) : !loaded ? (
        <div className="first__skeleton" aria-busy="true" />
      ) : <div className="cu__card" role="status"><p>{scheduleError || partial ? week.unavailable : week.empty}</p><button className="btn btn--primary" onClick={() => setReload((n) => n + 1)}>{c.retry}</button></div>}
      {partial && <p className="alert" role="status">{week.partial}</p>}
      {err && <p className="alert" role="alert">{err}</p>}

      {rows.length > 0 && <DiscoveryRows rows={rows} lang={l} {...(opening ? { busy: opening } : {})} onDirect={(g) => void start(g, g.kind !== 'tonight', g.kind !== 'tonight')} />}
      {replays === undefined && <div className="first__skeleton" aria-busy="true" />}
      <button className="first__setup" onClick={() => onSetup(l)} data-testid="first-run-setup">{c.setup} →</button>
    </main>
  );
}

function FeaturedStory({ card, c, lang, busy, name, onName, onGo, onDirect }: { card: FeedCard; c: ReturnType<typeof copy>; lang: DirectorLanguage; busy: boolean; name: string; onName: (s: string) => void; onGo: () => void; onDirect?: (() => void) | undefined }) {
  const week = weekCopy(lang);
  const lobby = card.kind === 'tonight';
  const style = { '--a': teamAccent(card.away.tricode), '--h': teamAccent(card.home.tricode) } as CSSProperties;
  return (
    <article className="feat" style={style} data-testid="featured-story" data-game-id={card.gameId} data-kind={card.kind}>
      <span className="feat__wash" aria-hidden="true" />
      <p className="feat__eyebrow"><i aria-hidden="true" />{card.kind === 'live' ? c.live : lobby ? week.upcoming : week.recent}</p>
      <div className="feat__teams">
        <TeamLogo tricode={card.away.tricode} size={48} />
        <span>{directorTeamName(card.away.tricode, lang)} <i>@</i> {directorTeamName(card.home.tricode, lang)}<small>{cardDate(card, lang)} · {cardStatus(card, lang)}</small></span>
        <TeamLogo tricode={card.home.tricode} size={48} />
      </div>
      <h2 className="feat__headline">{directorTeamName(card.away.tricode, lang)} @ {directorTeamName(card.home.tricode, lang)}</h2>
      {card.stars.length > 0 && (
        <div className="feat__stars">
          {card.stars.map((s) => {
            const code = s.side === 'home' ? card.home.tricode : card.away.tricode;
            return (
              <span key={s.personId} className="feat__star" style={{ '--t': teamAccent(code) } as CSSProperties}>
                <Headshot personId={s.personId} name={s.name} size={52} ring={teamAccent(code)} />
                <b>{s.name}</b>
                <small>{code}</small>
              </span>
            );
          })}
        </div>
      )}
      {(card.kind === 'final' || card.kind === 'live') && <p className="feat__score">{card.away.tricode} {card.away.score} – {card.home.score} {card.home.tricode}</p>}
      <p className="feat__tease">{lobby ? week.lobby : card.kind === 'final' ? week.recap : week.live}</p>
      <label className="feat__name">
        <span className="sr-only">{c.name}</span>
        <input value={name} maxLength={24} autoComplete="given-name" placeholder={c.name} onChange={(e) => onName(e.target.value)} disabled={busy} data-testid="first-run-name" />
      </label>
      <button className="latest__go feat__go" onClick={onGo} disabled={busy} data-testid="first-run-start">
        <span className="latest__play" aria-hidden="true">▶</span>
        <span><b>{busy ? c.opening : lobby ? week.join : c.cta}</b><small>{lobby ? cardStatus(card, lang) : c.sixty}</small></span>
      </button>
      {onDirect && <button className="feat__direct" onClick={onDirect} disabled={busy} data-testid="first-run-direct"><i aria-hidden="true" />{pick({ en: 'Direct this game', hi: 'यह मैच डायरेक्ट करें', zh: '导演这场比赛' }, lang)} →</button>}
      <p className="feat__then">{card.kind === 'final' ? week.full : lobby ? week.upcoming : c.live}</p>
    </article>
  );
}
